import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EnvironmentOutlined, MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { Button, Card, Empty } from "antd";
import Heading from "../../components/Heading";
import "../SearchResults/shared.scss";
import "./style.scss";

const getReadableValue = (value) => {
  if (value == null) return "";
  const normalized = String(value).trim();
  if (!normalized || normalized === "0" || normalized.toLowerCase() === "null") {
    return "";
  }
  return normalized;
};

const mergeValues = (...values) => values.filter(Boolean).join(" / ");

const HubSearch = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hubData, setHubData] = useState([]);

  useEffect(() => {
    const { hubData: data } = location.state || {};
    if (!data) {
      setHubData([]);
      return;
    }

    setHubData(Array.isArray(data) ? data : [data]);
  }, [location.state]);

  const searchTerm = location.state?.searchTerm || "";
  const contactableHubCount = hubData.filter((item) => {
    const officialNumber = getReadableValue(item?.officialNumber);
    const personalNumber = getReadableValue(item?.personalNumber);
    return Boolean(officialNumber || personalNumber);
  }).length;
  const emailableHubCount = hubData.filter((item) =>
    Boolean(getReadableValue(item?.email))
  ).length;

  const goBackToPrevScreen = () => {
    if (window.history.length <= 1) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="global-search-page hub-search-page">
      <div className="p-2-tab">
        <Heading
          title="Hub Search"
          showBack={true}
          goBack={goBackToPrevScreen}
        />
      </div>

      <div className="global-search-shell">
        <Card className="global-search-panel global-search-header-card" bordered={false}>
          <div className="global-search-title-row">
            <div className="global-search-title-block">
              <h1>{searchTerm || "Hub Search Results"}</h1>
            </div>
            {hubData.length ? (
              <span className="global-search-badge">{hubData.length} matches</span>
            ) : null}
          </div>
          <div className="global-search-actions">
            <Button onClick={goBackToPrevScreen}>Back</Button>
          </div>
        </Card>

        <div className="global-search-metric-grid">
          <Card className="global-search-metric" bordered={false}>
            <div className="global-search-metric-label">Results</div>
            <div className="global-search-metric-value">{hubData.length}</div>
          </Card>

          <Card className="global-search-metric" bordered={false}>
            <div className="global-search-metric-label">With Phone</div>
            <div className="global-search-metric-value">{contactableHubCount}</div>
          </Card>

          <Card className="global-search-metric" bordered={false}>
            <div className="global-search-metric-label">With Email</div>
            <div className="global-search-metric-value">{emailableHubCount}</div>
          </Card>
        </div>

        {hubData.length ? (
          <div className="global-search-result-grid">
            {hubData.map((item, index) => {
              const hubName = getReadableValue(item?.name);
              const address = getReadableValue(item?.address);
              const officialNumber = getReadableValue(item?.officialNumber);
              const personalNumber = getReadableValue(item?.personalNumber);
              const phone = mergeValues(officialNumber, personalNumber);
              const email = getReadableValue(item?.email);
              const pincode = getReadableValue(item?.pincode);

              const details = [
                { label: "Address", value: address },
                { label: "Phone", value: phone },
                { label: "Email", value: email },
                { label: "Pincode", value: pincode },
              ].filter((detail) => detail.value);

              return (
                <Card
                  key={`${item?.premiseId || hubName || "hub"}-${index}`}
                  className="global-search-result-card hub-result-card"
                  bordered={false}
                >
                  <div className="global-search-card-head">
                    <div>
                      <h2 className="global-search-card-title">
                        {hubName || `Hub ${index + 1}`}
                      </h2>
                    </div>
                    {pincode ? <span className="global-search-badge">{pincode}</span> : null}
                  </div>

                  {details.length ? (
                    <div className="global-search-detail-panel">
                      <h4>Details</h4>
                      <div className="global-search-meta-list">
                        {details.map((detail) => (
                          <div className="global-search-meta-item" key={detail.label}>
                            <div className="global-search-meta-label">{detail.label}</div>
                            <div className="global-search-meta-value">{detail.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="global-search-inline-links">
                    {officialNumber || personalNumber ? (
                      <Button href={`tel:${officialNumber || personalNumber}`}>
                        <PhoneOutlined /> Call
                      </Button>
                    ) : null}
                    {email ? (
                      <Button href={`mailto:${email}`}>
                        <MailOutlined /> Email
                      </Button>
                    ) : null}
                    {pincode ? (
                      <Button
                        onClick={() =>
                          navigate(`/pincode-map?pincode=${encodeURIComponent(pincode)}`)
                        }
                      >
                        <EnvironmentOutlined /> Open Pincode Map
                      </Button>
                    ) : null}
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="global-search-panel global-search-empty" bordered={false}>
            <Empty
              description={
                searchTerm
                  ? `No hub results were returned for "${searchTerm}".`
                  : "Run a hub lookup from global search to view results."
              }
            />
            <div className="global-search-actions global-search-actions-tight">
              <Button onClick={goBackToPrevScreen}>Back</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default HubSearch;
